import { useState, useCallback, useEffect } from 'react';
import { openAIService, Thread, Run } from '../services/openai.service';
import { InspectionsFindingsItemDto } from '../types/dtos/inspection';

interface UseOpenAIReturn {
  isLoading: boolean;
  error: Error | null;
  suggestedCriteria: Omit<InspectionsFindingsItemDto, 'findingId'>[] | null;
  getSuggestedCriteria: (findingContent: string) => Promise<void>;
}

interface OpenAICriterion {
  id: string;
  cpNumber: string;
}

export const useOpenAI = (): UseOpenAIReturn => {
  const [thread, setThread] = useState<Thread | null>(null);
  const [_run, setRun] = useState<Run | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [suggestedCriteria, setSuggestedCriteria] = useState<any[] | null>(null);

  // Cleanup thread when component unmounts
  useEffect(() => {
    return () => {
      // TODO: Implement thread cleanup when API is available
    };
  }, []);

  const transformOpenAICriteria = (openAICriteria: OpenAICriterion[]): any[] => {
    return openAICriteria.map(criterion => ({
      id: undefined,
      item: {
        id: criterion.id,
        cpNumber: criterion.cpNumber,
        description: '',
        name: '',
        versionId: '',
        programLevelId: '',
        measurementTypeId: ''
      },
      isActive: true
    }) as any);
  };

  const pollRunStatus = useCallback(async (threadId: string, runId: string) => {
    try {
      const maxAttempts = 30; // 30 seconds timeout
      let attempts = 0;

      while (attempts < maxAttempts) {
        const runStatus = await openAIService.getRun(threadId, runId);
        
        if (runStatus.status === 'completed') {
          const messages = await openAIService.getMessages(threadId);
          const assistantMessage = messages.data.find(m => m.role === 'assistant');
          
          if (assistantMessage) {
            try {
              const content = assistantMessage.content[0].text.value;
              console.log('OpenAI response:', content); // Debug log
              const parsedCriteria = JSON.parse(content) as OpenAICriterion[];
              console.log('Parsed criteria:', parsedCriteria); // Debug log
              const transformedCriteria = transformOpenAICriteria(parsedCriteria);
              console.log('Transformed criteria:', transformedCriteria); // Debug log
              setSuggestedCriteria(transformedCriteria);
            } catch (e) {
              console.error('Error parsing OpenAI response:', e); // Debug log
              setError(new Error('Failed to parse assistant response'));
            }
          }
          return;
        } else if (runStatus.status === 'failed' || runStatus.status === 'cancelled') {
          throw new Error(`Run failed with status: ${runStatus.status}`);
        }

        attempts++;
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before next poll
      }

      throw new Error('Timeout waiting for run completion');
    } catch (e) {
      if (e instanceof Error) {
        setError(e);
      } else {
        setError(new Error('An unknown error occurred'));
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getSuggestedCriteria = useCallback(async (findingContent: string) => {
    setIsLoading(true);
    setError(null);
    setSuggestedCriteria(null);

    try {
      // Create a new thread
      const newThread = await openAIService.createThread();
      setThread(newThread);

      // Add the finding content as a message
      await openAIService.addMessage(newThread.id, findingContent);

      // Create and start a run
      const newRun = await openAIService.createRun(newThread.id);
      setRun(newRun);

      // Start polling for run status
      await pollRunStatus(newThread.id, newRun.id);
    } catch (e) {
      if (e instanceof Error) {
        setError(e);
      } else {
        setError(new Error('An unknown error occurred'));
      }
      setIsLoading(false);
    }
  }, [pollRunStatus]);

  return {
    isLoading,
    error,
    suggestedCriteria,
    getSuggestedCriteria
  };
}; 